import css from './TableFilterable.module.scss';
import { forwardRef, useRef, useEffect, useState } from 'react';
import { useSortBy, useTable, useRowSelect, useGlobalFilter, useExpanded } from 'react-table';
import Button from '../atoms/Button';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';

//  for selecting rows / code by react-table
const IndeterminateCheckbox = forwardRef(({ indeterminate, ...rest }, ref) => {
  const defaultRef = useRef();
  const resolvedRef = ref || defaultRef;

  useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);

  return <input type="checkbox" ref={resolvedRef} {...rest} />;
});

export default function TableFilterable({
  columns,
  data,
  cellPadding = '16px',
  dataName,
  dataNamePlural,
  bulkActions,
  visibilityFilters,
  canCreate = true,
  renderRowSubComponent,
  createFunction,
  createLink,
  // setAction,
  status,
  itemsSelectable = true,
  showBar = true,

  handleMore,
  displayShowMore,
  loadingMore,
  count,
  setSelected,

  showBulk = true,
}) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    visibleColumns,
    selectedFlatRows,
    state: { selectedRowIds, globalFilter, expanded },
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      isMultiSortEvent: (x) => true, // to make multisort possible without holding shift
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    useRowSelect,
    (hooks) => {
      itemsSelectable &&
        hooks.visibleColumns.push((columns) => [
          // Let's make a column for selection
          {
            id: 'selectionColumn',
            width: '20px',
            textAlign: 'center',
            accessor: 'selectionColumn',
            disableSortBy: true,
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
            ),
            Cell: ({ row }) => <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />,
          },
          ...columns,
        ]);
    },
  );
  useEffect(() => {
    if (setSelected) setSelected(selectedFlatRows.map((row) => row.original._id));
  }, [selectedFlatRows]);

  // const customStyles = {
  //     option: (provided, state) => ({
  //       ...provided,
  //       borderBottom: '2px dotted green',
  //       color: state.isSelected ? 'yellow' : 'black',
  //       backgroundColor: state.isSelected ? 'green' : 'white'
  //     }),
  //     control: (provided) => ({
  //       ...provided,
  //       marginTop: "5%",
  //     })
  //   }

  return (
    <>
      {/* This search bar should be a separate component, but we need to find a way to pass props correctly so it works with react-table */}
      {showBar && (
        <div className={css['controls']}>
          <div className={css['search']}>
            {/* <div> */}
            <input
              onChange={(e) => setGlobalFilter(e.target.value)}
              value={globalFilter || ''}
              placeholder={`Search for ${dataNamePlural}`}
            />
            {/* <i className="fa fa-search" /> */}
            {/* </div> */}
            {canCreate && (
              <Button
                label={`Create ${dataName}`}
                iconLeft="fa fa-plus"
                action={createFunction}
                href={createLink}
              />
            )}
          </div>

          {(visibilityFilters?.length > 0 || bulkActions?.length > 0) && (
            <div className={css['actions']}>
              {bulkActions?.length > 0 && (
                <>
                  <Select
                    options={bulkActions}
                    placeholder="Bulk Actions"
                    className={css['custom-select']}
                    // value={aiction}
                    // to hide keyboard on mobile
                    isSearchable={false}
                  />
                  <Button
                    label={`Apply on ${selectedFlatRows.length} ${
                      selectedFlatRows.length === 1 ? dataName : dataNamePlural
                    }`}
                  />
                </>
              )}
              {visibilityFilters?.length && (
                <Select
                  options={visibilityFilters}
                  placeholder={`Select ${status} status`}
                  className={css['custom-select']}
                  // to hide keyboard on mobile
                  isSearchable={false}
                  // styles={customStyles}
                />
              )}
            </div>
          )}
        </div>
      )}

      <div className={css['container']}>
        <table {...getTableProps()} className={css['table']}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps(
                      column.getSortByToggleProps({
                        style: {
                          width: column.width,
                          padding: column.padding || '8px 16px',
                          textAlign: column.textAlign,
                        },
                      }),
                    )}
                  >
                    {column.render('Header')}
                    {column.canSort && (
                      <span
                        className={`${column.isSorted && css['sort-arrow--active']} ${
                          css['sort-arrow']
                        } `}
                      >
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <i className="fa fa-sort-down" />
                          ) : (
                            <i className="fa fa-sort-up" />
                          )
                        ) : (
                          <i className="fa fa-sort" />
                        )}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row, i) => {
              prepareRow(row);
              return (
                <>
                  <tr {...row.getRowProps()} className={row.isSelected ? css['selected'] : ''}>
                    {row.cells.map((cell) => {
                      return (
                        <td
                          // style={{
                          //     padding: `${cellPadding} 20px`,
                          // }}
                          {...cell.getCellProps({
                            style: {
                              width: cell.column.width,
                              padding: cell.column.padding || '8px 16px',
                              textAlign: cell.column.textAlign,
                            },
                          })}
                        >
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                  {row.isExpanded ? (
                    <tr>
                      <td colSpan={visibleColumns.length}>
                        {/*
                          Inside it, call our renderRowSubComponent function. In reality,
                          you could pass whatever you want as props to
                          a component like this, including the entire
                          table instance. But for this example, we'll just
                          pass the row
                        */}
                        {renderRowSubComponent({ row })}
                      </td>
                    </tr>
                  ) : null}
                </>
              );
            })}
          </tbody>
        </table>
        {displayShowMore && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 25,
              marginBottom: 25,
            }}
          >
            <Button label={loadingMore ? 'Loading...' : 'Show More'} action={handleMore} />
          </div>
        )}
      </div>
    </>
  );
}
